/**
 * N3 · Competência 1 (Números) · task-model H1.1.K02
 *
 *   "Ordenar números reais em diferentes representações."
 *
 * Cinco cartões — decimais, negativos, frações — que o aluno põe em ordem crescente.
 * Cada variante esconde duas ARMADILHAS, escolhidas porque cada uma corresponde a uma
 * concepção equivocada que o catálogo nomeia:
 *
 *  - dois negativos (−1,4 e −0,6), para quem ordena pelo módulo;
 *  - dois decimais com quantidades diferentes de casas (0,45 e 0,6), para quem compara
 *    pelo tamanho do número depois da vírgula;
 *  - duas frações de numerador 1 (1/5 e 1/3), para quem acha que denominador maior dá
 *    fração maior.
 *
 * O ERRO TÍPICO É UMA ORDEM INTEIRA: a certa, com o par da armadilha trocado. É a
 * sequência que um aluno com aquela concepção — e só ela — produziria. A ordem
 * decrescente também tem nome. A ordem de partida nunca é nenhuma dessas.
 */

import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  ORD_DECIMAL_TAMANHO,
  ORD_FRACAO_DENOMINADOR,
  ORD_INVERTIDA,
  ORD_NEGATIVOS,
} from '../../schema/errors.ts';
import type { ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

type Variante = 'negativos' | 'fracoes';
type Papel =
  'neg-longe' | 'neg-perto' | 'dec-curto' | 'dec-longo' | 'frac-grande' | 'frac-pequena' | 'outro';

interface Cartao {
  readonly texto: string;
  readonly valor: number;
  readonly papel: Papel;
}

export interface Params {
  readonly variante: Variante;
  /** Os cartões na ordem em que aparecem de início. */
  readonly cartoes: readonly Cartao[];
}

const dec = (v: number, casas: number): string => {
  const s = formatBR(Math.abs(v), casas);
  return v < 0 ? `−${s}` : s;
};

function sortear(g: Rng): Params | null {
  const variante: Variante = g.bool() ? 'negativos' : 'fracoes';
  // Dois decimais: 0,6 e 0,45 — o de duas casas é MENOR, mas "45" é maior que "6".
  const d1 = g.int(3, 9);
  const d2 = g.int(1, d1 - 1);
  const e = g.int(1, 9);
  const curto = d1 / 10;
  const longo = d2 / 10 + e / 100;
  const outro = 1 + g.int(1, 9) / 10;
  const cartoes: Cartao[] = [
    { texto: dec(curto, 1), valor: curto, papel: 'dec-curto' },
    { texto: dec(longo, 2), valor: longo, papel: 'dec-longo' },
    { texto: dec(outro, 1), valor: outro, papel: 'outro' },
  ];
  if (variante === 'negativos') {
    const longe = -(1 + g.int(1, 9) / 10);
    const perto = -g.int(2, 9) / 10;
    cartoes.push(
      { texto: dec(longe, 1), valor: longe, papel: 'neg-longe' },
      { texto: dec(perto, 1), valor: perto, papel: 'neg-perto' },
    );
  } else {
    const [a, b] = g
      .shuffle([2, 3, 4, 5, 6, 8])
      .slice(0, 2)
      .sort((x, y) => x - y) as [number, number];
    cartoes.push(
      { texto: `1/${a}`, valor: 1 / a, papel: 'frac-grande' },
      { texto: `1/${b}`, valor: 1 / b, papel: 'frac-pequena' },
    );
  }
  // Valores bem separados: dois cartões quase iguais virariam teste de conta, não de conceito.
  const ord = [...cartoes].sort((x, y) => x.valor - y.valor);
  if (ord.some((c, i) => i > 0 && c.valor - (ord[i - 1] as Cartao).valor < 0.03)) return null;
  // A armadilha só existe se o par estiver vizinho? Não precisa: a ordem errada troca o
  // par onde quer que ele esteja. Precisa só que a troca mude a ordem — e sempre muda.
  const embaralhados = g.shuffle(cartoes);
  const p: Params = { variante, cartoes: embaralhados };
  const certa = ordemCerta(p);
  const partida = embaralhados.map((_c, i) => i);
  if (igual(partida, certa) || errosDe(p).some((er) => igual(partida, er.ordem))) return null;
  return p;
}

const igual = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

function ordemCerta(p: Params): number[] {
  return p.cartoes
    .map((c, i) => ({ c, i }))
    .sort((x, y) => x.c.valor - y.c.valor)
    .map((x) => x.i);
}

/** A ordem certa com dois cartões trocados de lugar. */
function trocando(p: Params, a: Papel, b: Papel): number[] {
  const certa = ordemCerta(p);
  const ia = p.cartoes.findIndex((c) => c.papel === a);
  const ib = p.cartoes.findIndex((c) => c.papel === b);
  return certa.map((i) => (i === ia ? ib : i === ib ? ia : i));
}

function errosDe(p: Params): { ordem: number[]; error: ErrorCode; explanation: RichText }[] {
  const t = (papel: Papel): string => p.cartoes.find((c) => c.papel === papel)?.texto ?? '';
  const out: { ordem: number[]; error: ErrorCode; explanation: RichText }[] = [];
  const tamanho: RichText = [
    txt(
      `${t('dec-longo')} é menor que ${t('dec-curto')}: compare casa a casa, a partir dos décimos. `,
    ),
    txt('O número de algarismos depois da vírgula não diz qual decimal é maior.'),
  ];
  if (p.variante === 'negativos') {
    const neg: RichText = [
      txt(
        `Entre dois negativos, o menor é o que está mais longe do zero: ${t('neg-longe')} vem antes de ${t('neg-perto')}.`,
      ),
    ];
    out.push({
      ordem: trocando(p, 'neg-longe', 'neg-perto'),
      error: ORD_NEGATIVOS,
      explanation: neg,
    });
    // As duas armadilhas de uma vez: o nome vai para a dos negativos, e a explicação fala das duas.
    const dupla = trocando({ ...p, cartoes: p.cartoes }, 'neg-longe', 'neg-perto');
    const ia = p.cartoes.findIndex((c) => c.papel === 'dec-curto');
    const ib = p.cartoes.findIndex((c) => c.papel === 'dec-longo');
    out.push({
      ordem: dupla.map((i) => (i === ia ? ib : i === ib ? ia : i)),
      error: ORD_NEGATIVOS,
      explanation: [...neg, txt(' Além disso, '), ...tamanho],
    });
  } else {
    out.push({
      ordem: trocando(p, 'frac-grande', 'frac-pequena'),
      error: ORD_FRACAO_DENOMINADOR,
      explanation: [
        txt(
          `${t('frac-pequena')} é menor que ${t('frac-grande')}: com o numerador 1, quanto maior o denominador, menores as partes.`,
        ),
      ],
    });
  }
  out.push({
    ordem: trocando(p, 'dec-curto', 'dec-longo'),
    error: ORD_DECIMAL_TAMANHO,
    explanation: tamanho,
  });
  out.push({
    ordem: [...ordemCerta(p)].reverse(),
    error: ORD_INVERTIDA,
    explanation: [txt('A ordem pedida é crescente: do menor para o maior.')],
  });
  return out;
}

export const ordenarReais: ItemTemplate<Params, null> = {
  id: templateId('h1-01-K02-ordenar'),
  version: 1,
  taskModel: taskModelCode('H1.1.K02'),
  skills: [skillCode('H1.1')],
  axis: 'I',
  level: 'N3',
  title: 'Ordenar números reais',
  responseModel: '2PL',
  difficultySources: ['steps.2', 'two-supports'],
  params: (rng) =>
    sampleWhere(rng.fork('cartoes'), sortear, (p) => p !== null, {
      label: 'cartoes',
      maxTries: 300,
    }) as Params,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.variante === 'fracoes') fontes.push('non-round-numbers');
    return fontes;
  },
  build(p) {
    const certa = ordemCerta(p);
    const nomes = certa.map((i) => p.cartoes[i]?.texto ?? '');
    return {
      // O enunciado descreve o que está NOS cartões. Dizia "escritos de formas
      // diferentes" nas duas variantes — e a de negativos é só de decimais, em 55% das
      // vezes: o aluno procurava a fração que o texto prometia.
      stem: [
        txt(
          p.variante === 'negativos'
            ? 'Os cartões abaixo trazem números decimais, positivos e negativos.'
            : 'Os cartões abaixo trazem números escritos de formas diferentes: decimais e frações.',
        ),
      ],
      comando: [
        txt('Coloque os cartões em '),
        strong('ordem crescente'),
        txt(', do menor para o maior — arrastando-os ou usando os botões ↑ e ↓.'),
      ],
      stimuli: [],
      interaction: {
        kind: 'order',
        itens: p.cartoes.map((c) => [txt(c.texto)]),
        ordem: certa,
        pontas: ['menor', 'maior'],
        erros: errosDe(p),
      },
      rationale: [
        strong('Resolução. '),
        txt(`A ordem crescente é ${nomes.join(' < ')}.`),
        br,
        // Cada variante explica só as armadilhas que tem: falar de negativos a quem só
        // viu frações, ou de formas misturadas a quem só viu decimais, é ruído na hora
        // em que o aluno mais precisa de foco.
        ...(p.variante === 'negativos'
          ? [
              txt('Os negativos vêm primeiro, e entre eles o mais longe do zero é o menor. '),
              txt('Entre decimais, compara-se casa a casa a partir dos décimos.'),
            ]
          : [
              // Os dois decimais de tamanhos diferentes estão nas duas variantes, e o
              // distrator que os troca também: a regra deles vale aqui igualmente.
              txt('Entre decimais, compara-se casa a casa a partir dos décimos. '),
              txt(
                'Entre frações de numerador 1, a de denominador maior é a menor: o inteiro foi repartido em mais pedaços. ',
              ),
              txt(
                'Com frações e decimais misturados, vale escrever todos como decimais para comparar.',
              ),
            ]),
      ],
      declared: 'media',
    };
  },
};
